import {
  Box,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { Controller } from "react-hook-form";
import ImageDropUpload from "../../../shared/ImageDropUpload";
import { ProductTitle } from "../steps/components/ProductTitle";
import {
  getIssueOptions,
  getResolutionOptions,
  isImageUploadRequired,
} from "./options";
import { baseTheme } from "../../../themes/shipinsure";

export function applyPackageItemValues(group, setValue, patch) {
  group.items.forEach(({ formIndex, lineItem }, i) => {
    setValue(`items.${formIndex}.issueType`, patch.issueType, {
      shouldDirty: true,
    });
    setValue(`items.${formIndex}.itemId`, lineItem.id);
    setValue(`items.${formIndex}.quantity`, lineItem.quantity);
    setValue(`items.${formIndex}.reason`, patch.reason ?? "");
    setValue(`items.${formIndex}.resolution`, patch.resolution ?? "");
    setValue(`items.${formIndex}.images`, i === 0 ? (patch.images ?? []) : []);
  });
}

export function PackageClaimFields({
  group,
  order,
  t,
  control,
  setValue,
  getValues,
  register,
  watch,
  itemsErrors,
}) {
  const leadIndex = group.items[0].formIndex;
  const currentReason = watch(`items.${leadIndex}.reason`);
  const issueOptions = getIssueOptions("insurance", order.merchant_issues);
  const resolutionOptions = getResolutionOptions(
    issueOptions,
    currentReason,
    order.store_credits_enabled,
  );

  return (
    <Box mt="8px" p="24px" bgcolor="#F4F4F9" borderRadius="8px">
      {group.items.map(({ lineItem, formIndex }) => (
        <Box mb="18px" key={`${group.id}-${formIndex}`}>
          <Grid container>
            <ProductTitle item={lineItem} title={t("resolve.variant")} />
          </Grid>
        </Box>
      ))}
      <Grid container rowSpacing={2} mt="10px">
        <Grid item xs={12}>
          <Controller
            name={`items.${leadIndex}.reason`}
            defaultValue=""
            control={control}
            rules={{
              validate: (value) => (!value ? t("resolve.required") : true),
            }}
            render={({ field }) => (
              <FormControl
                fullWidth
                data-claim-field={`items.${leadIndex}.reason`}
              >
                <InputLabel>{t("resolve.form.issue")}*</InputLabel>
                <Select
                  label={`${t("resolve.form.issue")}*`}
                  value=""
                  {...field}
                  onChange={(event) => {
                    field.onChange(event);
                    const reason = event.target.value;
                    applyPackageItemValues(group, setValue, {
                      issueType: "insurance",
                      reason,
                      resolution: "",
                      images: [],
                    });
                  }}
                  error={!!itemsErrors?.[leadIndex]?.reason}
                >
                  {issueOptions.map((issue, i) => (
                    <MenuItem key={i} value={issue.issue_name}>
                      {issue.issue_name}
                    </MenuItem>
                  ))}
                </Select>
                {itemsErrors?.[leadIndex]?.reason && (
                  <FormHelperText error>
                    {itemsErrors[leadIndex].reason.message}
                  </FormHelperText>
                )}
              </FormControl>
            )}
          />
        </Grid>
        {isImageUploadRequired(currentReason, order.merchant_issues) && (
          <Grid item xs={12} data-claim-field={`items.${leadIndex}.images`}>
            <ImageDropUpload
              images={getValues(`items.${leadIndex}.images`) ?? []}
              onImageChange={(images) => {
                applyPackageItemValues(group, setValue, {
                  issueType: "insurance",
                  reason: getValues(`items.${leadIndex}.reason`),
                  resolution: getValues(`items.${leadIndex}.resolution`),
                  images,
                });
                setValue(`items.${leadIndex}.images`, images, {
                  shouldValidate: images.length ? true : false,
                });
              }}
              text={t.rich("resolve.upload", {
                text: (chunk) => <Typography>{chunk}</Typography>,
                cta: (chunk) => (
                  <Typography component="span" color={baseTheme.colors.primary}>
                    {chunk}
                  </Typography>
                ),
              })}
            />
            <input
              type="hidden"
              {...register(`items.${leadIndex}.images`, {
                validate: (value) =>
                  (value || []).length === 0 ? t("resolve.required") : true,
              })}
            />
            {itemsErrors?.[leadIndex]?.images && (
              <FormHelperText error>
                {itemsErrors[leadIndex].images.message}
              </FormHelperText>
            )}
          </Grid>
        )}
        {currentReason && (
          <Grid item xs={12}>
            <Controller
              name={`items.${leadIndex}.resolution`}
              control={control}
              defaultValue=""
              rules={{
                validate: (value) => (!value ? t("resolve.required") : true),
              }}
              render={({ field }) => (
                <FormControl
                  fullWidth
                  data-claim-field={`items.${leadIndex}.resolution`}
                >
                  <InputLabel>{t("resolve.form.resolution")}*</InputLabel>
                  <Select
                    label={`${t("resolve.form.resolution")}*`}
                    value=""
                    {...field}
                    onChange={(event) => {
                      field.onChange(event);
                      applyPackageItemValues(group, setValue, {
                        issueType: "insurance",
                        reason: getValues(`items.${leadIndex}.reason`),
                        resolution: event.target.value,
                        images: getValues(`items.${leadIndex}.images`) ?? [],
                      });
                    }}
                    error={!!itemsErrors?.[leadIndex]?.resolution}
                  >
                    {resolutionOptions.map((option, i) => (
                      <MenuItem key={i} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                  {itemsErrors?.[leadIndex]?.resolution && (
                    <FormHelperText error>
                      {itemsErrors[leadIndex].resolution.message}
                    </FormHelperText>
                  )}
                </FormControl>
              )}
            />
          </Grid>
        )}
      </Grid>
    </Box>
  );
}
